/**
 * Which banner click to record, as a pure function of what was clicked.
 *
 * The DOM side lives in src/hooks/useCtaBannerTracking.ts; this half is separate so
 * it can be tested without a browser (vitest runs in node here).
 *
 * Three kinds of clickable area:
 *   - [data-cta-banner]  FinalCTASection, the dark blue banner on ~40 pages. The
 *                        attribute value is its preset ('trial', 'demo', ...) or 'custom'.
 *   - [data-cta-button]  a button placed inside an article with the blog editor
 *                        (CTAButtonExtension), rendered from the post HTML.
 *   - [data-cta-section] any other server-rendered group of buttons that wants its
 *                        clicks counted under a named place, e.g. the home page hero
 *                        ('home_hero'). Recorded under the plain button text.
 *
 * Labels carry a prefix because get_cta_attribution groups clicks by label alone, and
 * "View Pricing" or "Start Free Trial" also exist outside banners.
 */
import { CTA_PRESETS } from '@/components/conversion/ctaPresets';

export const BANNER_PREFIX = 'Banner · ';
export const ARTICLE_BUTTON_PREFIX = 'Article button · ';

/** Every label a preset banner can produce, prefixed as it will be recorded. */
export const BANNER_PRESET_LABELS = Array.from(
  new Set(
    Object.values(CTA_PRESETS).flatMap((p) => [p.ctaText, p.secondaryText]).map((t) => BANNER_PREFIX + t),
  ),
).sort();

export interface BannerClickInput {
  href: string;
  /** The anchor's visible text. */
  text: string;
  /** data-cta-banner value of the enclosing banner, or null when not inside one. */
  bannerVariant: string | null;
  /** True when the anchor is inside a [data-cta-button] from the blog editor. */
  inArticleButton: boolean;
  /** data-cta-section value of the enclosing group, or null when not inside one. */
  section?: string | null;
}

export interface BannerClickEvent {
  destination: string;
  label: string;
  section: string;
  location: string;
}

function cleanLabel(text: string, fallback: string): string {
  return text.trim().replace(/\s+/g, ' ').slice(0, 120) || fallback;
}

export function bannerClickEvent(input: BannerClickInput): BannerClickEvent | null {
  if (input.bannerVariant !== null) {
    return {
      destination: input.href,
      label: BANNER_PREFIX + cleanLabel(input.text, 'Button'),
      section: 'cta_banner',
      location: input.bannerVariant || 'custom',
    };
  }
  if (input.inArticleButton) {
    return {
      destination: input.href,
      label: ARTICLE_BUTTON_PREFIX + cleanLabel(input.text, 'Button'),
      section: 'article_button',
      location: 'content',
    };
  }
  if (input.section) {
    return {
      destination: input.href,
      label: cleanLabel(input.text, 'Button'),
      section: input.section,
      location: 'above_fold',
    };
  }
  return null;
}

/** Everything this listener records; the product-link listener skips these. */
export const BANNER_SELECTOR = '[data-cta-banner], [data-cta-button], [data-cta-section]';
